import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { Tabs } from '@heroui/react';
import { DateRangeField } from '@/components/dateRangeField';
import type { WindowNote } from './booksWindowMeta';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { BooksOutletContext } from './booksOutletContext';
import type { LinkedTab } from '../../tabHref';
import type { BooksTab, BooksTabId } from './booksTabsMeta';

type BooksPageViewProps = {
    tabs: LinkedTab<BooksTab>[];
    selectedTabId: BooksTabId;
    fromDate: string;
    toDate: string;
    onRangeChange: (fromDate: string, toDate: string) => void;
    /** Handed to the open tab through the outlet — the shell's date range. */
    /** What the window means on the open tab (BOS-166). */
    note: WindowNote;
    outletContext: BooksOutletContext;
};

/**
 * Books shell — render only. The date range sits above the tab bar because it
 * scopes every tab, not just the open one; moving it inside a panel would read
 * as a filter belonging to that one report.
 *
 * Each tab's content is the routed `<Outlet />`. It appears in every panel, but
 * only the selected panel mounts, so exactly one child route renders — the tab
 * bar and the URL can't disagree.
 */
export const BooksPageView: React.FC<BooksPageViewProps> = ({
    tabs,
    selectedTabId,
    fromDate,
    toDate,
    onRangeChange,
    outletContext,
    note,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('accounting.books.title')}
            className="flex w-full flex-col gap-5"
            {...getNodeTestIdProp('books-section')}
        >
            <div className="flex w-full flex-col gap-1">
                <div className="w-full min-w-52 max-w-72">
                    <DateRangeField
                        ariaLabel={t('accounting.books.rangeAria')}
                        fromDate={fromDate}
                        onChange={onRangeChange}
                        slot="books"
                        toDate={toDate}
                    />
                </div>
                {/* One picker, two readings — movement over the window on
                    Journal and VAT, a balance as of its end on the other two.
                    Only the trial balance used to say which (BOS-166). */}
                <span
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('books-window-note')}
                >
                    {t(note.key, note.params)}
                </span>
            </div>
            {/* No `onSelectionChange`: each tab is a real link now, and
                React Aria does not report the clicked key through it for link
                tabs — it fires with the key already selected, or not at all.
                Wiring it to a navigate would send the user to the tab they
                were already on. The URL is the single source of truth and
                `selectedKey` reads it. */}
            <Tabs
                className="w-full"
                selectedKey={selectedTabId}
                variant="secondary"
            >
                <Tabs.ListContainer className="min-w-0 max-w-max">
                    <Tabs.List aria-label={t('accounting.books.tabsAria')}>
                        {tabs.map((tab) => (
                            // `.tabs__tab` is `w-full` with no nowrap, so on a
                            // narrow screen the four labels share the width and
                            // wrap mid-name ("CHART OF / ACCOUNTS"). Sized to
                            // their text instead, they overflow — which is what
                            // the list container's scroller is for.
                            <Tabs.Tab
                                className="whitespace-nowrap"
                                href={tab.href}
                                id={tab.id}
                                key={tab.id}
                                {...getNodeTestIdProp(`books-tab-${tab.id}`)}
                            >
                                {t(tab.labelKey)}
                                <Tabs.Indicator />
                            </Tabs.Tab>
                        ))}
                    </Tabs.List>
                </Tabs.ListContainer>
                {tabs.map((tab) => (
                    <Tabs.Panel
                        className="pt-4"
                        id={tab.id}
                        key={tab.id}
                        {...getNodeTestIdProp(`books-panel-${tab.id}`)}
                    >
                        <Outlet context={outletContext} />
                    </Tabs.Panel>
                ))}
            </Tabs>
        </section>
    );
};
